// @ts-nocheck

import React, { useCallback, useMemo, useEffect, useRef, useState, useLayoutEffect } from 'react';
import styled, { css } from 'styled-components';

import { BigBoostedOddCompactTextureImgPath } from '../../assets/img/boosted-odd';
import { Target } from '../../client';
import {
  BigBoostedOddSpritesheetPathsByLangByTournamentId,
  BigBoostedOddLogoByLang,
} from '../../constants/boostedOdds';
import {
  MOBILE_BOOSTED_ODD_PADDING,
  TIMEOUT_REPLAY_BOOSTED_ODD_ANIMATION,
  TIMEOUT_START_BOOSTED_ODD_ANIMATION,
} from '../../constants/ui/boostedOdds';
import { useViewportContext } from '../../contexts/ViewportContext';
import { getLang } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import { CardModes } from '../../types/common';
import { getRandomBoostedOddImgInfosByMatchId } from '../../utils/boostedOdds';
import BoostedOddButton from '../BoostedOddButton';
import Spritesheet from '../Spritesheet';

const CARD_MODE = CardModes.BIG_BOOSTED;

const Wrapper = styled.div`
  border-radius: 0 0 1em 1em;
  border-top: none;
  overflow: hidden;
`;

const ImageWrapper = styled.div`
  width: 100%;
  padding-top: 33.33%;
  position: relative;
  pointer-events: none;
  z-index: 9;
`;

const Image = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 50%;
  pointer-events: none;
`;

const ContentWrapper = styled.div`
  position: relative;

  padding: 0.4em 1em 1em;
  background: ${theme.BoostedOddCard.backgroundColor};
  border-radius: 0 0 1em 1em;

  text-align: center;

  background: rgb(14, 10, 33);

  ${({ $backgroundImgPath }) => css`
    ${$backgroundImgPath
      ? css`
          background-image: url(${$backgroundImgPath});
          background-size: cover;
          background-position: center center;
          background-repeat: no-repeat;
        `
      : undefined}
  `}
`;

const StyledBigBoostedOddLogo = styled.svg`
  margin: 0.3em 0 0em;
  max-width: 16em;
  height: 3.3em;
`;

const WrapTitle = styled.div`
  position: relative;
  margin: 0.4em 0 1em 0;
`;

const Title = styled.div`
  font-size: 0.95em;
  font-weight: bold;
  color: ${theme.BoostedOddCard.title.color};
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const SubTitle = styled.div`
  color: ${theme.BoostedOddCard.subTitle.color};
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-size: 1.1em;
`;

const BoostedOddRow = styled.div`
  display: flex;
`;

type BigBoostedOddCardContentProps = {
  className?: string;
  matchId: number;
  image: string | null;
  subTitle: string | null;
  oldValue?: number;
  newValue: number;
  outcomeLabel: string;
  onClick: (...params: unknown) => unknown;
  betId: number;
  oddId: number;
  selected?: boolean;
  uuid?: string;
  tournamentId?: number;
  isCompact?: boolean;
};

const BigBoostedOddCardContent = ({
  className,
  matchId,
  image,
  subTitle,
  oldValue,
  newValue,
  outcomeLabel,
  onClick,
  betId,
  oddId,
  selected,
  uuid,
  tournamentId,
  isCompact = false,
}: BigBoostedOddCardContentProps) => {
  const lang = useMemo(() => getLang(), []);
  const Logo = useMemo(() => BigBoostedOddLogoByLang[lang], [lang]);

  const { width } = useViewportContext();
  const [spriteSheetImgPath, setSpriteSheetImgPath] = useState({});
  const [play, setPlay] = useState(0);

  // Spritesheet timeouts
  const spriteSheetStartTimeout = useRef();
  const spriteSheetReplayTimeout = useRef();

  const wrapperRef = useRef();
  const [adaptativeWidth, setAdaptiveWidth] = useState();

  useLayoutEffect(() => {
    setAdaptiveWidth(wrapperRef?.current?.offsetWidth);
  }, [width]);

  // mecanism to force a component to re-render with hooks
  // @see https://newbedev.com/how-can-i-force-component-to-re-render-with-hooks-in-react
  const [, updateState] = useState();
  const forceUpdate = useCallback(() => updateState({}), []);

  const handleClickBoostedOddButton = useCallback(
    () => onClick(matchId, betId, oddId, selected),
    [betId, matchId, oddId, onClick, selected],
  );

  // ANIMATION ONLY FOR BIG ODD BOOSTED
  // mecanism to replay the animation after a given delay
  const replayAnimationOnDone = () => {
    if (image) {
      return;
    }

    // clear of timeout to avoid unexpected timeout side effects
    if (spriteSheetReplayTimeout.current) {
      clearTimeout(spriteSheetReplayTimeout.current);
    }

    spriteSheetReplayTimeout.current = setTimeout(forceUpdate, TIMEOUT_REPLAY_BOOSTED_ODD_ANIMATION);
  };

  // set of spritesheet url depending on the lang
  useEffect(() => {
    if (image) {
      return;
    }

    setSpriteSheetImgPath(
      getRandomBoostedOddImgInfosByMatchId(
        matchId,
        BigBoostedOddSpritesheetPathsByLangByTournamentId[lang]?.[tournamentId] ?? [],
      ) ?? {},
    );
  }, [image, lang, matchId, tournamentId]);

  // mecanism to start the boosted odd animation after it has been mounted
  // ONLY for any of those conditions:
  // - 1 we don't have a teaser
  // - 2 we have a big boosted odd
  // - 3 the lang is authorized
  // - 4 the sport category is authorized
  useEffect(() => {
    if (image) {
      return;
    }

    // clear of timeout to avoid unexpected timeout side effects
    if (spriteSheetStartTimeout.current) {
      clearTimeout(spriteSheetStartTimeout.current);
    }

    spriteSheetStartTimeout.current = setTimeout(() => {
      setPlay(1);
    }, TIMEOUT_START_BOOSTED_ODD_ANIMATION);
  }, [image]);

  useEffect(() => {
    return () => {
      clearTimeout(spriteSheetStartTimeout.current);
      clearTimeout(spriteSheetReplayTimeout.current);
    };
  }, []);

  return (
    <Wrapper ref={wrapperRef} className={className}>
      {!isCompact ? (
        image ? (
          <ImageWrapper>
            <Image src={image} />
          </ImageWrapper>
        ) : spriteSheetImgPath ? (
          <Spritesheet
            columnCount={3}
            duration={500}
            keepRatio
            onDone={replayAnimationOnDone}
            play={play}
            rowCount={5}
            url={spriteSheetImgPath}
            width={Target.__MOBILE__ ? width - MOBILE_BOOSTED_ODD_PADDING : adaptativeWidth}
          />
        ) : null
      ) : null}

      <ContentWrapper $backgroundImgPath={isCompact ? BigBoostedOddCompactTextureImgPath : null}>
        {isCompact ? <StyledBigBoostedOddLogo as={Logo} /> : null}
        <WrapTitle>
          {subTitle ? <Title>{subTitle}</Title> : null}
          {outcomeLabel ? <SubTitle>{outcomeLabel}</SubTitle> : null}
        </WrapTitle>
        <BoostedOddRow>
          {oldValue ? <BoostedOddButton value={oldValue} selected={selected} cardMode={CARD_MODE} /> : null}
          <BoostedOddButton
            onClick={handleClickBoostedOddButton}
            oldValue={oldValue}
            selected={selected}
            cardMode={CARD_MODE}
            value={newValue}
            oddId={oddId}
            uuid={uuid}
            isNewValue
          />
        </BoostedOddRow>
      </ContentWrapper>
    </Wrapper>
  );
};

export default React.memo<BigBoostedOddCardContentProps>(BigBoostedOddCardContent);
